import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  FileText, ChartLineUp, ArrowRight, Sparkle, Trophy, Warning, Broadcast,
} from "@phosphor-icons/react";

// Marketing Intelligence Phase 3 — Content Performance.
// Reads /api/marketing/content-performance. No writes, no new data collection.

function fmtNum(n) {
  return Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
}
function fmtMoney(n) {
  return `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}
function fmtRate(v) {
  return v === null || v === undefined ? "—" : `${v}%`;
}

export default function ContentPerformance() {
  const { currentOrg } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/marketing/content-performance", { params: { days } });
      setData(data);
    } catch {
      setData(null);
    } finally { setLoading(false); }
  }, [days]);
  useEffect(() => { load(); }, [load, currentOrg?.org_id]);

  return (
    <div className="p-8 md:p-12 max-w-[1400px]" data-testid="content-performance-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary flex items-center gap-1.5">
            <FileText weight="duotone" className="h-3 w-3" /> Marketing · Content
          </div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight">Content performance</h1>
          <p className="mt-3 text-sm text-ink-secondary max-w-2xl">
            Aggregated from inbound marketing data — matches each row to a content item by title.
            Untouched content is never listed as underperforming; you only see the pieces actually shipped.
          </p>
        </div>
        <Link to="/marketing" className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-graphite-950 px-4 py-2 text-sm text-ink-primary hover:bg-graphite-900"
          data-testid="content-performance-back">
          Marketing overview <ArrowRight weight="bold" className="h-3.5 w-3.5" />
        </Link>
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-ink-secondary" data-testid="content-performance-loading">
          Loading content performance…
        </div>
      ) : !data ? (
        <div className="mt-10 text-sm text-ink-secondary">Content performance unavailable.</div>
      ) : !data.has_data ? (
        <div className="mt-10 rounded-md border border-white/10 bg-graphite-900 p-12 text-center"
          data-testid="content-performance-empty">
          <FileText weight="duotone" className="mx-auto h-10 w-10 text-ink-secondary/60" />
          <div className="mt-4 font-display text-2xl tracking-tight">No content performance data yet</div>
          <p className="mt-2 text-sm text-ink-secondary max-w-md mx-auto">
            Once inbound marketing data flows in (via the Metricool inbound API) and you have Content items
            with matching titles, this page will surface top and underperforming pieces.
          </p>
        </div>
      ) : (
        <>
          <div className="mt-8 text-[10px] uppercase tracking-[0.2em] text-ink-secondary" data-testid="content-perf-window">
            Last {data.days} days · {data.row_count} row{data.row_count === 1 ? "" : "s"}
          </div>

          {/* Top + Worst performers */}
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
            <PerformerCard
              title="Top performers"
              testid="content-perf-top"
              icon={Trophy}
              tone="text-emerald-300"
              rows={data.top_performers}
              emptyText="No performers with data yet."
              rowTestid={(r, i) => `content-perf-top-${i}`}
            />
            <PerformerCard
              title="Underperforming"
              testid="content-perf-worst"
              icon={Warning}
              tone="text-amber-300"
              rows={data.worst_performers}
              emptyText="Nothing underperforming — all published content has some traction."
              rowTestid={(r, i) => `content-perf-worst-${i}`}
            />
          </div>

          {/* Platform + Type breakdowns */}
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
            <BreakdownTable
              title="By platform"
              testid="content-perf-by-platform"
              rowKey="platform"
              rows={data.by_platform}
              cols={[
                { key: "impressions", label: "Impressions", align: "right", fmt: fmtNum },
                { key: "engagement", label: "Engagement", align: "right", fmt: fmtNum },
                { key: "engagement_rate", label: "Eng %", align: "right", fmt: fmtRate },
                { key: "clicks", label: "Clicks", align: "right", fmt: fmtNum },
              ]}
              icon={Broadcast}
            />
            <BreakdownTable
              title="By content type"
              testid="content-perf-by-type"
              rowKey="content_type"
              rows={data.by_content_type}
              cols={[
                { key: "impressions", label: "Impressions", align: "right", fmt: fmtNum },
                { key: "engagement", label: "Engagement", align: "right", fmt: fmtNum },
                { key: "engagement_rate", label: "Eng %", align: "right", fmt: fmtRate },
                { key: "clicks", label: "Clicks", align: "right", fmt: fmtNum },
              ]}
              icon={Sparkle}
              emptyText="No linked content has a type set yet."
            />
          </div>
        </>
      )}
    </div>
  );
}

function PerformerCard({ title, testid, icon: Icon, tone, rows, emptyText, rowTestid }) {
  return (
    <section className="rounded-md border border-white/10 bg-graphite-900 p-6" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon weight="duotone" className={`h-4 w-4 ${tone}`} />
        <h2 className="font-display text-lg tracking-tight">{title}</h2>
      </div>
      {rows.length === 0 ? (
        <div className="mt-4 text-sm text-ink-secondary" data-testid={`${testid}-empty`}>{emptyText}</div>
      ) : (
        <ol className="mt-4 space-y-2">
          {rows.map((r, i) => (
            <li key={`${r.content_name}-${i}`}
                data-testid={rowTestid(r, i)}
                className="rounded-md border border-white/10 bg-graphite-950/40 px-4 py-3">
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-ink-primary truncate">{r.content_name}</div>
                  <div className="mt-0.5 text-[10px] uppercase tracking-widest text-ink-secondary flex items-center gap-2">
                    {r.content_type && (
                      <span className="border border-white/10 px-1.5 py-0.5 rounded-sm">{r.content_type}</span>
                    )}
                    {!r.is_linked && (
                      <span className="text-amber-300/80">unlinked</span>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase tracking-widest text-ink-secondary">Engagement</div>
                  <div className="text-sm tabular-nums text-ink-primary">{fmtNum(r.engagement)}</div>
                </div>
                <div className="text-right shrink-0 w-20">
                  <div className="text-[10px] uppercase tracking-widest text-ink-secondary">Eng %</div>
                  <div className="text-sm tabular-nums text-emerald-200/90">{fmtRate(r.engagement_rate)}</div>
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function BreakdownTable({ title, testid, rowKey, rows, cols, icon: Icon, emptyText }) {
  return (
    <section className="rounded-md border border-white/10 bg-graphite-900 p-6" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon weight="duotone" className="h-4 w-4 text-ink-primary/80" />
        <h2 className="font-display text-lg tracking-tight">{title}</h2>
      </div>
      {rows.length === 0 ? (
        <div className="mt-4 text-sm text-ink-secondary" data-testid={`${testid}-empty`}>
          {emptyText || "No data yet."}
        </div>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-ink-secondary border-b border-white/10 bg-graphite-950/40">
                <th className="px-4 py-2 font-medium">{rowKey === "platform" ? "Platform" : "Type"}</th>
                {cols.map((c) => (
                  <th key={c.key} className={`px-4 py-2 font-medium text-${c.align || "left"}`}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {rows.map((r) => (
                <tr key={r[rowKey]} data-testid={`${testid}-row-${r[rowKey]}`} className="hover:bg-white/[0.02]">
                  <td className="px-4 py-2 text-ink-primary">{r[rowKey]}</td>
                  {cols.map((c) => (
                    <td key={c.key} className={`px-4 py-2 text-${c.align || "left"} text-ink-secondary tabular-nums`}>
                      {c.fmt ? c.fmt(r[c.key]) : r[c.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
